import React from 'react';

import {
  ListColSectionsDebuggingSession,
  ListColSectionsInspections,
  ListColSectionsTestCases,
} from './constants';
import { UsageDetailsTableHeader } from './list-header';
import { UsageDetailsListItem } from './list-item';
import { TestPlanList } from '../../hooks/test-plan.hooks';
import { MobileInspection } from '@modules/test-development/pages/mobile-inspector/models/mobile-inspection.model';
import { Empty, useTranslation } from '@testsigma/atoms';
import noDataPane from '@images/no-data-pane.svg';
import { Agent } from '@models/agent.model';
import {
  DebuggerUsageDetails,
  TestPlanLicenseUsage,
} from '@modules/test-development/models/license-usage.model';
import { withStartStopModal } from '@hocs';

interface UsageTableProps {
  activeTab: string;
  inspections?: MobileInspection[];
  testPlanLicenseUsageList?: TestPlanLicenseUsage[];
  debuggingSessionUsageList?: DebuggerUsageDetails[];
  refresh?: () => void;
  onClose?: (isClose: boolean) => void;
  onClickReportAgent?: (
    testPlanId: string | number,
  ) => (e: React.MouseEvent) => void;
  testPlanIdVsAgents?: Record<string, Agent[]>;
  // Props injected by withStartStopModal HOC (always provided with defaults)
  checkRunningDebuggerSession: (session: any) => void;
}

const UsageTableBase: React.FC<UsageTableProps> = ({
  activeTab,
  testPlanLicenseUsageList,
  debuggingSessionUsageList,
  inspections,
  refresh,
  onClose,
  onClickReportAgent,
  testPlanIdVsAgents,
  checkRunningDebuggerSession,
}) => {
  const { t } = useTranslation();

  const resultTable = TestPlanList('');

  return (
    <>
      {activeTab == 'testplan' && (
        <UsageDetailsTableHeader columnDetails={ListColSectionsTestCases} />
      )}
      {activeTab == 'mobileInspection' && (
        <UsageDetailsTableHeader columnDetails={ListColSectionsInspections} />
      )}
      {activeTab == 'debuggingSession' && (
        <UsageDetailsTableHeader
          columnDetails={ListColSectionsDebuggingSession}
        />
      )}

      <div className='h-[calc(100%_-_44px)] overflow-y-auto flex flex-wrap content-baseline'>
        {activeTab == 'testplan' &&
          testPlanLicenseUsageList?.map((data, index) => (
            <div key={index} className='flex flex-wrap basis-full w-full'>
              <UsageDetailsListItem
                columnDetails={resultTable.testplanItemGenerator(
                  [data],
                  refresh,
                  onClose,
                  onClickReportAgent,
                  testPlanIdVsAgents,
                )}
              />
            </div>
          ))}
        {activeTab == 'mobileInspection' &&
          inspections?.map((data, index) => (
            <div key={index} className='flex flex-wrap basis-full w-full'>
              <UsageDetailsListItem
                columnDetails={resultTable.mobileInspactionGenerator(
                  [data],
                  refresh,
                )}
              />
            </div>
          ))}
        {activeTab == 'debuggingSession' &&
          debuggingSessionUsageList?.map((data, index) => (
            <div key={index} className='flex flex-wrap basis-full w-full'>
              <UsageDetailsListItem
                columnDetails={resultTable.debuggingSessionGenerator(
                  [data],
                  checkRunningDebuggerSession,
                )}
              />
            </div>
          ))}

        {activeTab == 'testplan' &&
          !testPlanLicenseUsageList?.length &&
          !inspections?.length && (
            <Empty
              className='w-full'
              description={t('runs.nothing')}
              image={
                <img src={noDataPane} alt={t('common.no_data.illustration')} />
              }
            />
          )}

        {activeTab == 'mobileInspection' && !inspections?.length && (
          <Empty
            className='w-full'
            description={t('inspections.nothing')}
            image={
              <img src={noDataPane} alt={t('common.no_data.illustration')} />
            }
          />
        )}
        {activeTab == 'debuggingSession' &&
          !debuggingSessionUsageList?.length && (
            <Empty
              className='w-full'
              description={t('debugging_sessions.empty_list.message')}
              image={
                <img src={noDataPane} alt={t('common.no_data.illustration')} />
              }
            />
          )}
      </div>
    </>
  );
};

// Export the component wrapped with the HOC
export const UsageTable = withStartStopModal(UsageTableBase);
